import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { LoadingOverlay } from 'components/loading/LoadingOverlay';
import { setApiBaseUrl } from 'helpers/axios/axios';
import { FeatureToggleType } from './types';
import config from '../../Config';
import { FEATURE_TOGGLE } from '../../constants';

const MAX_CARDINALITY = 10000;
const SESSION_KEY_STORAGE_NAME = 'checkout_split_session_key';
const TREATMENT_ON = 'on';

/**
 * Returns a random session key with bounded cardinality, persisted in sessionStorage so the same
 * key (and therefore the same Split treatments, e.g. which BFF is used) is kept across reloads
 * and redirects within a browser tab. Falls back to a non-persisted key if storage is unavailable.
 */
const generateSessionKey = (): string => {
  try {
    const storedKey = window.sessionStorage.getItem(SESSION_KEY_STORAGE_NAME);
    if (storedKey) {
      return storedKey;
    }
  } catch {
    // sessionStorage can throw when blocked by the browser; use a transient key instead
  }
  const randomIndex = Math.floor(Math.random() * MAX_CARDINALITY);
  const newKey = `session-${randomIndex}`;
  try {
    window.sessionStorage.setItem(SESSION_KEY_STORAGE_NAME, newKey);
  } catch {
    // ignore, key stays in memory only
  }
  return newKey;
};

const factory: SplitIO.IBrowserSDK = SplitFactory({
  core: {
    authorizationKey: config.SPLIT_API_KEY,
    key: Cookies.get('user_id_myob_checkout') || generateSessionKey()
  }
});
const client = factory.client();
const FeatureToggleContext = createContext({} as FeatureToggleType);
export const useFeatureToggleContext = (): FeatureToggleType => useContext(FeatureToggleContext);

export const FeatureToggleProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { search } = location;
  const { productId, 'use-legacy': useLegacy } = qs.parse(search, { ignoreQueryPrefix: true });
  // Register SDK_READY listener once on mount
  useEffect(() => {
    client.on(client.Event.SDK_READY, () => {
      // Must be resolved before children render so every API call targets the selected BFF
      const useAzureBff = client.getTreatment(FEATURE_TOGGLE.BFF_AZURE) === TREATMENT_ON;
      setApiBaseUrl(useAzureBff ? config.API_BASE_URL_AZURE : config.API_BASE_URL);
      setIsLoading(false);
    });
  }, []);

  const attributes = useMemo(() => ({
    productId: String(productId)
  }), [productId]);

  const isFeatureEnabled = useCallback((feature: string) => {
    if (feature === FEATURE_TOGGLE.FRONT_BOOK_LIVE && typeof useLegacy === 'string') {
      return useLegacy === 'false';
    }
    const treatment = client.getTreatment(feature, attributes);
    return treatment === 'on';
  }, [attributes, useLegacy]);

  return (
    <FeatureToggleContext.Provider value={{ isFeatureEnabled }}>
      { isLoading ? <LoadingOverlay isLoading={true} /> : children }
    </FeatureToggleContext.Provider>
  );
};